import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { 
  Zap, 
  Pause, 
  Play, 
  Settings, 
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronRight,
  Loader2
} from "lucide-react";

export default function AutoPilotStatusWidget({ onConfigure }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  
  useEffect(() => {
    fetchStatus();
  }, []);
  
  const fetchStatus = async () => {
    try {
      const res = await apiClient.get("/playbook/status");
      setStatus(res.data);
    } catch (err) {
      console.error("Failed to fetch Auto-Pilot status:", err);
    } finally {
      setLoading(false);
    }
  };
  
  const toggleAutoPilot = async () => {
    if (!status) return;
    
    setToggling(true);
    try {
      await apiClient.post("/playbook/toggle", { enabled: !status.active });
      await fetchStatus();
    } catch (err) {
      console.error("Failed to toggle Auto-Pilot:", err);
    } finally {
      setToggling(false);
    }
  };
  
  if (loading) {
    return (
      <div className="glass-card p-6 rounded-xl border border-white/10">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-viraly-muted" />
        </div>
      </div>
    );
  }
  
  // Not configured state OR user has explicitly paused (deactivated)
  // Show "Set Up Auto-Pilot" widget only when:
  // 1. Auto-Pilot has never been configured (inactive)
  // 2. User has deactivated it (paused_user) - to encourage re-activation
  // DO NOT show when Auto-Pilot is active (even if paused due to credits)
  const shouldShowSetupWidget = !status || status.status === "inactive" || status.status === "paused_user";
  
  if (shouldShowSetupWidget) {
    const isPausedByUser = status?.status === "paused_user";
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-6 rounded-xl border border-white/10 hover:border-viraly-orange/30 transition-all cursor-pointer"
        onClick={onConfigure}
        data-testid="auto-pilot-setup-widget"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-viraly-orange/20 to-orange-500/20 flex items-center justify-center">
            <Zap className="w-6 h-6 text-viraly-orange" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-viraly-light">
              {isPausedByUser ? "Re-activate Auto-Pilot" : "Set Up Auto-Pilot"}
            </h3>
            <p className="text-sm text-viraly-muted">
              {isPausedByUser 
                ? "Your Auto-Pilot is paused. Re-activate to resume automatic content creation"
                : "Let Viraly create content for you automatically"}
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-viraly-muted" />
        </div>
      </motion.div>
    );
  }
  
  // Active or Paused state
  const isActive = status.active && status.status === "active";
  const isPausedCredits = status.status === "paused_credits";
  const isPausedUser = status.status === "paused_user";
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`glass-card p-6 rounded-xl border transition-all ${
        isActive ? 'border-viraly-success/30 bg-viraly-success/5' :
        isPausedCredits ? 'border-yellow-500/30 bg-yellow-500/5' :
        'border-white/10'
      }`}
      data-testid="auto-pilot-status-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isActive ? 'bg-viraly-success/20' :
            isPausedCredits ? 'bg-yellow-500/20' :
            'bg-white/10'
          }`}>
            {isActive ? (
              <CheckCircle className="w-5 h-5 text-viraly-success" />
            ) : isPausedCredits ? (
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
            ) : (
              <Pause className="w-5 h-5 text-viraly-muted" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-viraly-light flex items-center gap-2">
              Auto-Pilot
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                isActive ? 'bg-viraly-success/20 text-viraly-success' :
                isPausedCredits ? 'bg-yellow-500/20 text-yellow-500' :
                'bg-white/10 text-viraly-muted'
              }`}>
                {isActive ? "ACTIVE" : isPausedCredits ? "PAUSED" : "PAUSED"}
              </span>
            </h3>
            {isPausedCredits && (
              <p className="text-xs text-yellow-400">Credits exhausted</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleAutoPilot}
            disabled={toggling}
            className={isActive ? "text-viraly-success" : "text-viraly-muted"}
          >
            {toggling ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isActive ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4" />
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={onConfigure}>
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Status Details */}
      {isActive && status.next_post && (
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
            <Clock className="w-4 h-4 text-viraly-muted" />
            <div className="flex-1">
              <p className="text-sm text-viraly-light">Next post</p>
              <p className="text-xs text-viraly-muted">
                {new Date(status.next_post.scheduled_at).toLocaleString()} • {status.next_post.platform}
              </p>
            </div>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-viraly-muted">This week:</span>
            <span className="text-viraly-light">
              {status.posts_this_week} of {status.expected_posts_per_week} posts
            </span>
          </div>
          
          {status.current_theme && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-viraly-muted">Theme:</span>
              <span className="text-viraly-orange">{status.current_theme}</span>
            </div>
          )}
        </div>
      )}
      
      {/* Credits Exhausted Warning */}
      {isPausedCredits && (
        <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <p className="text-sm text-yellow-200 mb-2">
            Auto-Pilot is paused because credits ran out.
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => window.location.href = "/pricing"}>
              Top Up Now
            </Button>
            <Button size="sm" variant="ghost" className="text-yellow-400" onClick={onConfigure}>
              Enable Auto-Charge
            </Button>
          </div>
        </div>
      )}
      
      {/* Quick Actions */}
      {isActive && (
        <div className="flex gap-2 mt-4 pt-4 border-t border-white/10">
          <Button variant="ghost" size="sm" className="flex-1" onClick={() => window.location.href = "/calendar"}>
            <Calendar className="w-4 h-4 mr-1" />
            View Calendar
          </Button>
        </div>
      )}
    </motion.div>
  );
}
